import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { pb } from '../../../pocketbase'
import { COL_CONSUMABLE_CATEGORIES, COL_CONSUMABLES } from '../../../config/collections'
import { UNIT_OPTIONS } from '../../../lib/units'
import type { ConsumableCategoryRecord, ConsumableRecord } from '../../../types/records'
import { FormField } from '../../../components/ui/FormField'
import { PageHeader } from '../../../components/ui/PageHeader'
import { alert, btn, formActionsClass, formGridClass, inputClass } from '../../../lib/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'
import { consumableSavePatch } from '../../../lib/pbRecordDefaults'

export function ConsumableFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNew = id === 'new' || !id

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [unit, setUnit] = useState('pcs')
  const [price, setPrice] = useState('')
  const [notes, setNotes] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [categories, setCategories] = useState<ConsumableCategoryRecord[]>([])
  const [catInit, setCatInit] = useState(false)
  const [loadedRow, setLoadedRow] = useState<ConsumableRecord | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(!isNew)

  const loadCategories = useCallback(async () => {
    try {
      const list = await pb.collection(COL_CONSUMABLE_CATEGORIES).getFullList<ConsumableCategoryRecord>({
        sort: 'sort,name',
      })
      setCategories(list)
    } catch {
      setCategories([])
    }
  }, [])

  const load = useCallback(async () => {
    if (!id || isNew) return
    setLoading(true)
    setErr('')
    try {
      const row = await pb.collection(COL_CONSUMABLES).getOne<ConsumableRecord>(id)
      setName(row.name)
      setCode(row.code || '')
      setUnit(row.unit || 'pcs')
      setPrice(String(row.price ?? ''))
      setNotes(row.notes || '')
      setCategoryId(typeof row.consumable_category === 'string' ? row.consumable_category : '')
      setLoadedRow(row)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void loadCategories()
  }, [loadCategories])

  useEffect(() => {
    if (!isNew || catInit || categories.length === 0) return
    const p = searchParams.get('category')
    if (p && categories.some((c) => c.id === p)) setCategoryId(p)
    setCatInit(true)
  }, [isNew, catInit, categories, searchParams])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr('')
    const priceNum = Number(price.replace(',', '.'))
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      setErr('Укажите корректную цену.')
      return
    }
    const body: Record<string, unknown> = {
      name: name.trim(),
      code: code.trim(),
      unit,
      price: priceNum,
      notes: notes.trim(),
    }
    if (categoryId) body.consumable_category = categoryId
    else body.consumable_category = null
    try {
      if (isNew) {
        await pb.collection(COL_CONSUMABLES).create(consumableSavePatch(null, body))
      } else {
        await pb.collection(COL_CONSUMABLES).update(id!, consumableSavePatch(loadedRow, body))
      }
      navigate('/production/consumables', { replace: true })
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка сохранения'))
    }
  }

  return (
    <div>
      <PageHeader asSection title={isNew ? 'Новый расходник' : 'Расходник'} />
      {err && <div className={alert('err')}>{err}</div>}
      {loading && !isNew ? (
        <p>Загрузка…</p>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className={formGridClass}>
          <FormField label="Наименование" htmlFor="c-name">
            <input
              id="c-name"
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={300}
            />
          </FormField>
          <FormField label="Код (артикул)" htmlFor="c-code">
            <input
              id="c-code"
              className={inputClass}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={100}
            />
          </FormField>
          <FormField label="Категория комплектации" htmlFor="c-category">
            <select
              id="c-category"
              className={inputClass}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">— не выбрано —</option>
              {[...categories]
                .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </FormField>
          <FormField label="Единица измерения" htmlFor="c-unit">
            <select
              id="c-unit"
              className={inputClass}
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              required
            >
              {UNIT_OPTIONS.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Цена за 1 ед., ₽" htmlFor="c-price">
            <input
              id="c-price"
              type="text"
              inputMode="decimal"
              className={inputClass}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
              placeholder="0.00"
            />
          </FormField>
          <FormField label="Примечание" htmlFor="c-notes">
            <textarea
              id="c-notes"
              className={inputClass}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </FormField>
          <div className={formActionsClass}>
            <button type="submit" className={btn('primary')}>
              Сохранить
            </button>
            <Link to="/production/consumables" className={btn('ghost')}>
              Отмена
            </Link>
          </div>
        </form>
      )}
    </div>
  )
}
